import { isEmpty } from "@swiggy-private/js-utils";
import { SORT_DISH_ID, SORT_ID_FILTERS } from "./constants";
import {
    IFacet,
    IFacetInfo,
    IFacetInfoTransform,
    IFacetList,
    IQueryInfo,
    IRefineList,
    ISortConfigs,
    ISortConfigsTransform,
} from "./types";

export const getSelectedSortValue = (sortConfigs: ISortConfigs[]): string => {
    return sortConfigs?.find((o: ISortConfigs) => o.selected === true)?.key ?? "";
};
export const getSelectedSortLabel = (sortConfigs: ISortConfigs[], key: string): string => {
    return sortConfigs?.find((o: ISortConfigs) => o.key === key)?.title ?? "";
};

export const getDefaultSortValue = (sortConfigs: ISortConfigs[]): string => {
    return sortConfigs?.find((o: ISortConfigs) => o.defaultSelection === true)?.key ?? "";
};

export const getAllSelectedFilter = (
    filterList: IFacetList[],
    sortList: ISortConfigs[]
): Record<string, string[]> => {
    const filterListContent = filterList?.reduce((acc, { id, facetInfo }) => {
        if (!facetInfo) return acc;
        // eslint-disable-next-line @typescript-eslint/no-shadow
        const selected = facetInfo?.filter(({ selected }) => selected).map(({ id }) => id);
        if (selected.length > 0) acc[id] = selected;
        return acc;
    }, {} as Record<string, string[]>);

    const sortFilter = sortList?.filter(
        (sortItem) => sortItem.selected === true && sortItem.defaultSelection !== true
    );

    const sortListContent: Record<string, string[]> = {};
    if (!isEmpty(sortFilter)) {
        sortListContent[SORT_ID_FILTERS] = [sortFilter[0]?.key];
    }
    return { ...filterListContent, ...sortListContent };
};

export const getAllSelectedFilterByLabel = (
    filterList: IFacetList[],
    sortList: ISortConfigs[],
    newSelected?: Record<string, string[]>
): Record<string, string[]> => {
    const filterListContent = filterList?.reduce((acc, { label, facetInfo, id }) => {
        if (!facetInfo) {
            acc[label] = ["-"];
            return acc;
        }
        if (newSelected) {
            if (newSelected[id] && newSelected[id].length > 0) {
                acc[label] = [...newSelected[id]];
            } else {
                acc[label] = ["-"];
            }
        } else {
            const selected = facetInfo?.filter((item) => item.selected).map((item) => item.id);
            if (selected.length > 0) {
                acc[label] = selected;
            } else {
                acc[label] = ["-"];
            }
        }
        return acc;
    }, {} as Record<string, string[]>);

    const sortFilter = sortList?.filter(
        (sortItem) => sortItem.selected === true && sortItem.defaultSelection !== true
    );

    const sortListContent: Record<string, string[]> = {};
    if (!isEmpty(sortFilter)) {
        sortListContent[SORT_ID_FILTERS] = [sortFilter[0]?.key];
    }
    return { ...filterListContent, ...sortListContent };
};

//gets list of filters with corresponding label values
export const getAllSelectedDishFilterByLabel = (
    filterList: IFacet[],
    sortList: ISortConfigs[],
    newSelected?: Record<string, IQueryInfo[]>,
    sortKey?: string
): Record<string, string[]> => {
    const filterListContent = filterList?.reduce((acc, { label, facetInfo, id }) => {
        if (!facetInfo) {
            acc[label] = ["-"];
            return acc;
        }
        if (newSelected) {
            if (newSelected[id] && newSelected[id].length > 0) {
                // eslint-disable-next-line @typescript-eslint/no-shadow
                const data = newSelected[id].map((data) => data.id);
                acc[label] = [...data];
            } else {
                acc[label] = ["-"];
            }
        } else {
            // eslint-disable-next-line @typescript-eslint/no-shadow
            const selected = facetInfo?.filter(({ selected }) => selected).map(({ id }) => id);
            if (selected.length > 0) {
                acc[label] = selected;
            } else {
                acc[label] = ["-"];
            }
        }

        return acc;
    }, {} as Record<string, string[]>);

    const sortFilter = sortList?.filter(
        (sortItem) => sortItem.selected === true && sortItem.defaultSelection !== true
    );

    const sortListContent: Record<string, string[]> = {};
    if (!isEmpty(sortFilter)) {
        sortListContent[SORT_DISH_ID] = [sortFilter[0]?.key];
    }
    if (sortKey) sortListContent[SORT_DISH_ID] = [sortKey];
    return { ...filterListContent, ...sortListContent };
};

export const getOpenFilterList = (facetList: IFacetList[]): IRefineList[] => {
    return (facetList || []).reduce(
        (acc: IRefineList[], { id, openFilter, facetInfo, icon, selectedIcon }) => {
            if (!openFilter && facetInfo) {
                const filteredFacetInfo = (facetInfo || [])
                    .filter(({ openFilter: innerOpenFilter }) => innerOpenFilter)
                    .map((obj2) => ({
                        ...obj2,
                        parentId: id,
                        icon: icon,
                        selectedIcon: selectedIcon,
                    }));
                return acc.concat(filteredFacetInfo);
            }
            return acc;
        },
        []
    );
};

export function transformSortObject(input: ISortConfigs[]): ISortConfigsTransform[] {
    if (isEmpty(input)) return [];

    return input.map(({ key, title, ...params }) => ({
        value: key,
        label: title,
        ...params,
    }));
}

export function transformFilterObject(input: IFacetInfo[]): IFacetInfoTransform[] {
    if (isEmpty(input)) return [];
    return input.map(({ id, ...params }) => ({
        value: id,
        ...params,
    }));
}

export function cleanFilterData(input: IFacetList[]): IFacetList[] {
    return input?.filter((item) => item.facetInfo);
}

export type RectResult = {
    bottom: number;
    height: number;
    left: number;
    right: number;
    top: number;
    width: number;
} | null;

export const getBoundingClient = (element: HTMLElement | null): RectResult | null => {
    if (!element) return null;
    return element.getBoundingClientRect();
};
